import { useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Gauge } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { errMsg, fmtDay, useApi, useMe } from "@/lib/api";

const ProActions = ({ me }) => {
  const api = useApi();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const sub = me.subscription || {};
  const act = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const portal = () => act(async () => {
    const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
    window.location.href = data.url;
  });
  const toggleCancel = () => act(async () => {
    if (!sub.cancel_at_period_end && !window.confirm("Cancel Pro? You keep unlimited exports until the end of this billing period.")) return;
    await api.post(sub.cancel_at_period_end ? "/billing/resume" : "/billing/cancel");
    await qc.invalidateQueries({ queryKey: ["me"] });
    toast.success(sub.cancel_at_period_end ? "Subscription resumed" : "Subscription will end at period end");
  });
  return (
    <div className="flex flex-wrap gap-2">
      {me.has_billing && (
        <button data-testid="manage-billing-btn" disabled={busy} onClick={portal} className="h-10 px-4 rounded-md border border-white/30 text-white text-sm font-semibold hover:bg-white/10 transition-colors disabled:opacity-60">
          Manage billing
        </button>
      )}
      <button data-testid="toggle-cancel-btn" disabled={busy} onClick={toggleCancel} className="h-10 px-4 rounded-md text-sm font-semibold text-slate-300 hover:bg-white/10 hover:text-white transition-colors disabled:opacity-60">
        {sub.cancel_at_period_end ? "Resume subscription" : "Cancel subscription"}
      </button>
    </div>
  );
};

const YEARLY_SAVINGS = { pro_monthly: 9.99 * 12 - 99.99, team_seat_monthly: 9.99 * 12 - 99.99, firm5_monthly: 39.99 * 12 - 399.99 };
const YEARLY_PRICE = { pro_monthly: 99.99, team_seat_monthly: 99.99, firm5_monthly: 399.99 };

const AnnualNudge = ({ sub }) => {
  const api = useApi();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const per = YEARLY_SAVINGS[sub.lookup_key];
  if (per === undefined || sub.cancel_at_period_end) return null;
  const qty = sub.lookup_key === "team_seat_monthly" ? sub.seats || 1 : 1;
  const save = (per * qty).toFixed(2);
  const yearly = (YEARLY_PRICE[sub.lookup_key] * qty).toFixed(2);
  const go = async () => {
    if (!window.confirm(`Switch to yearly billing for $${yearly}/year? Stripe charges the yearly price now, minus credit for the unused part of this month.`)) return;
    setBusy(true);
    try {
      await api.post("/billing/switch-yearly");
      await qc.invalidateQueries({ queryKey: ["me"] });
      toast.success(`Switched to yearly - you're saving $${save} a year`);
    } catch (e) {
      toast.error(errMsg(e, "Couldn't switch plans"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-md bg-amber-400/15 border border-amber-400/40 px-4 py-3" data-testid="annual-nudge">
      <p className="text-sm text-amber-100 flex-1">
        Switch to yearly and <span className="font-bold text-amber-300" data-testid="annual-nudge-savings">save ${save} a year</span> (${yearly}/year).
      </p>
      <button data-testid="switch-yearly-btn" disabled={busy} onClick={go} className="h-9 px-4 rounded-md bg-amber-400 text-[#1E293B] text-sm font-bold hover:bg-amber-300 transition-colors disabled:opacity-60 whitespace-nowrap">
        {busy ? "Switching..." : "Switch to yearly"}
      </button>
    </div>
  );
};

export default function PlanCard() {
  const { data: me, isLoading } = useMe();
  if (isLoading || !me) return <div className="h-36 bg-white border border-slate-200 rounded-lg animate-pulse" data-testid="plan-card-loading" />;
  const u = me.usage;
  const pro = u.plan === "pro";
  const sub = me.subscription || {};
  const pct = pro ? 100 : Math.min(100, (u.used / u.limit) * 100);
  const viaFirm = u.source === "firm";
  const isTeamOwner = sub.kind === "firm";
  const planName = viaFirm ? `Pro via ${me.firm?.name || "your team"}`
    : isTeamOwner ? `${(sub.lookup_key || "").startsWith("firm5") ? "Firm" : "Team"} plan - ${sub.seats} seats - ${sub.interval === "year" ? "Yearly" : "Monthly"}`
    : `Pro ${sub.interval === "year" ? "Yearly" : "Monthly"}`;

  return (
    <section data-testid="plan-card" className={`rounded-lg border p-6 flex flex-col md:flex-row md:items-center gap-6 justify-between ${pro ? "bg-[#1E293B] text-white border-[#1E293B]" : "bg-white border-slate-200"}`}>
      <div className="flex items-start gap-4">
        <div className={`w-11 h-11 rounded-md grid place-items-center ${pro ? "bg-amber-400 text-[#1E293B]" : "bg-blue-50 text-blue-600"}`}>
          {pro ? <Crown size={20} /> : <Gauge size={20} />}
        </div>
        <div>
          <div className="font-heading text-xl font-bold" data-testid="plan-name">{pro ? planName : "Free plan"}</div>
          {pro ? (
            <p className="text-sm text-slate-300 mt-1" data-testid="plan-detail">
              Unlimited exports. {!viaFirm && sub.current_period_end && (sub.cancel_at_period_end ? `Ends ${fmtDay(sub.current_period_end)}.` : `Renews ${fmtDay(sub.current_period_end)}.`)} {u.used} exports this cycle.
            </p>
          ) : (
            <p className="text-sm text-slate-600 mt-1" data-testid="plan-detail">
              <span className="font-semibold text-slate-900 tabular-nums" data-testid="usage-count">{u.used} of {u.limit}</span> exports used. Resets {fmtDay(u.period_end)}.
            </p>
          )}
          {pro && !viaFirm && <AnnualNudge sub={sub} />}
          {!pro && (
            <div className="mt-3 h-2 w-64 max-w-full bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${u.remaining ? "bg-blue-600" : "bg-red-500"}`} style={{ width: `${pct}%` }} data-testid="usage-bar" />
            </div>
          )}
        </div>
      </div>
      {pro ? (viaFirm ? (
        <Link to="/team" data-testid="view-team-btn" className="h-10 px-4 rounded-md border border-white/30 text-white text-sm font-semibold hover:bg-white/10 transition-colors flex items-center">View team</Link>
      ) : (
        <div className="flex flex-wrap gap-2">
          {isTeamOwner && <Link to="/team" data-testid="manage-team-btn" className="h-10 px-4 rounded-md bg-amber-400 text-[#1E293B] text-sm font-semibold hover:bg-amber-300 transition-colors flex items-center">Manage team</Link>}
          <ProActions me={me} />
        </div>
      )) : (
        <Link to="/pricing" data-testid="upgrade-btn" className="h-11 px-5 rounded-md bg-[#1E293B] text-white text-sm font-semibold hover:bg-blue-600 transition-colors flex items-center justify-center">
          Upgrade for unlimited
        </Link>
      )}
    </section>
  );
}
